import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  Star, Users, MapPin, Video, Loader2, CheckCircle2, ShieldCheck, ShieldAlert, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import WorkspaceLayout from '@/components/WorkspaceLayout';
import { MENTOR_NAV } from '@/lib/workspaceNav';
import { STATUS_COLORS, STATUS_LABELS, fmtTime, todayLocal } from './mentorData';

export function MentorWorkspace({ children, testId }) {
  return (
    <WorkspaceLayout nav={MENTOR_NAV} activeRole="mentor" testId={testId}>
      {children}
    </WorkspaceLayout>
  );
}

export function MentorLoading() {
  return (
    <MentorWorkspace testId="mentor-loading">
      <div className="py-24 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#F97D6A]" /></div>
    </MentorWorkspace>
  );
}

export function NoMentorProfile() {
  const navigate = useNavigate();
  return (
    <MentorWorkspace testId="mentor-no-profile">
      <div className="max-w-md py-16">
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight mb-3">Become a Career Mentor</h1>
        <p className="text-base text-[#6B7280] mb-7 leading-relaxed">Share your professional experience and help students at your school explore real careers.</p>
        <Button onClick={() => navigate('/mentor/onboarding')} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-11 px-6 font-semibold" data-testid="start-onboarding-btn">
          Create Mentor Profile
        </Button>
      </div>
    </MentorWorkspace>
  );
}

export function RatingStars({ value, size = 'h-4 w-4' }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`${size} ${i <= Math.round(value) ? 'text-amber-400 fill-amber-400' : 'text-gray-200'}`} />
      ))}
    </div>
  );
}

export function SectionHeading({ title, action, onAction, actionTestId }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4">
      <h2 className="text-base md:text-lg font-bold text-[#1F2937] font-['Outfit'] tracking-tight">{title}</h2>
      {action && (
        <button onClick={onAction} className="text-xs font-semibold text-[#F97D6A] hover:text-[#F06651] flex items-center gap-0.5" data-testid={actionTestId}>
          {action} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function ExperienceRow({ exp, featured = false, onComplete }) {
  const navigate = useNavigate();
  const isPast = exp.date && exp.date < todayLocal();
  const awaitingCompletion = isPast && ['confirmed', 'scheduled'].includes(exp.status);

  return (
    <div
      className={`group rounded-2xl border border-[#EFF0F2] bg-white transition-all hover:border-[#E0E2E6] hover:shadow-[0_2px_14px_rgba(16,24,40,0.06)] ${featured ? 'p-5 sm:p-6' : 'p-4'}`}
      data-testid={`experience-${exp.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`font-bold text-[#1F2937] font-['Outfit'] truncate ${featured ? 'text-lg' : 'text-sm'}`}>{exp.career || 'Career Experience'}</p>
            <Badge className={`border-0 text-[10px] shrink-0 ${awaitingCompletion ? 'bg-amber-50 text-amber-700' : (STATUS_COLORS[exp.status] || 'bg-gray-100 text-gray-500')}`}>
              {awaitingCompletion ? 'Awaiting Completion' : (STATUS_LABELS[exp.status] || exp.status)}
            </Badge>
          </div>
          <div className={`flex items-center gap-3 flex-wrap text-[#6B7280] mt-1.5 ${featured ? 'text-sm' : 'text-xs'}`}>
            <span className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-[#9CA3AF]" />
              {exp.source === 'session' ? `${exp.studentCount} student${exp.studentCount === 1 ? '' : 's'}` : (exp.childName || '1 student')}
            </span>
            <span className="flex items-center gap-1.5">
              {exp.offline ? <MapPin className="h-3.5 w-3.5 text-[#9CA3AF]" /> : <Video className="h-3.5 w-3.5 text-[#9CA3AF]" />}
              {exp.offline ? 'Workplace Visit' : 'Online Session'}
            </span>
          </div>
          <p className={`text-[#9CA3AF] mt-1 ${featured ? 'text-sm' : 'text-xs'}`}>
            {exp.date ? formatDate(exp.date) : 'Date to be scheduled'}{exp.time ? ` · ${fmtTime(exp.time)}` : ''}
          </p>
          {exp.feedback_rating && (
            <div className="mt-3 rounded-xl bg-[#FAFAFB] px-3 py-2.5" data-testid={`experience-feedback-${exp.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <RatingStars value={exp.feedback_rating} size="h-3.5 w-3.5" />
                {exp.feedback_helpful !== null && exp.feedback_helpful !== undefined && (
                  <Badge className={`border-0 text-[9px] ${exp.feedback_helpful ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {exp.feedback_helpful ? 'Helpful' : 'Not helpful'}
                  </Badge>
                )}
              </div>
              {exp.feedback_comment && <p className="text-sm text-[#4B5563] leading-relaxed">"{exp.feedback_comment}"</p>}
              {exp.feedback_submitted_at && <p className="text-[10px] text-[#9CA3AF] mt-1">{formatDate(exp.feedback_submitted_at)}</p>}
            </div>
          )}
          {exp.studentFeedback?.length > 0 && (
            <div className="mt-3 space-y-2" data-testid={`session-feedback-${exp.id}`}>
              {exp.studentFeedback.map((f, i) => (
                <div key={i} className="rounded-xl bg-[#FAFAFB] px-3 py-2.5">
                  <div className="flex items-center gap-2 mb-1">
                    <RatingStars value={f.feedback_rating} size="h-3.5 w-3.5" />
                    <span className="text-[10px] text-[#9CA3AF]">{f.child_name}</span>
                  </div>
                  {f.feedback_comment && <p className="text-sm text-[#4B5563] leading-relaxed">"{f.feedback_comment}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(exp.link)}
            className="rounded-full h-8 px-3.5 text-xs border-[#E5E7EB]"
            data-testid={`view-details-${exp.id}`}
          >
            View Details
          </Button>
          {awaitingCompletion && onComplete && (
            <Button
              size="sm"
              onClick={() => onComplete(exp)}
              className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full h-8 px-3.5 text-xs font-semibold"
              data-testid={`complete-${exp.id}`}
            >
              Complete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function CompleteExperienceDialog({ exp, open, onOpenChange, onDone }) {
  const navigate = useNavigate();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!exp) return;
    if (exp.source === 'session') { navigate(exp.link); return; }
    setSaving(true);
    try {
      await api.put(`/bookings/${exp.id}/complete`, { session_notes: notes || null });
      toast.success('Experience marked as completed');
      onOpenChange(false);
      setNotes('');
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to complete'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="complete-experience-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-[#1F2937]">Complete Experience</DialogTitle>
          <DialogDescription>
            Mark as completed: {exp?.career}{exp?.childName ? ` with ${exp.childName}` : ''}{exp?.date ? ` on ${formatDate(exp.date)}` : ''}.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[#1F2937] font-medium text-sm">Session Notes (optional)</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Brief notes about the session..." className="mt-1.5 rounded-xl border-gray-200 min-h-[80px] resize-none" data-testid="completion-notes" />
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full" data-testid="confirm-complete-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Mark Completed
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeclarationDialog({ open, onOpenChange, version, onDone }) {
  const [accepting, setAccepting] = useState(false);
  const [doc, setDoc] = useState(null);

  // One authoritative source: the same document the backend hashes into the evidence
  useEffect(() => {
    if (open && !doc) api.get('/consent/documents/mentor_declaration').then(r => setDoc(r.data)).catch(() => {});
  }, [open, doc]);

  const accept = async () => {
    setAccepting(true);
    try {
      await api.post('/mentor-profile/accept-declaration');
      toast.success('Safeguarding declaration accepted');
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to accept declaration'); }
    setAccepting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[80vh] overflow-y-auto" data-testid="declaration-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-[#1F2937] flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#F97D6A]" /> Safeguarding Declaration</DialogTitle>
          <DialogDescription>Review and accept the current safeguarding declaration (v{doc?.version || version})</DialogDescription>
        </DialogHeader>
        <div className="bg-[#FAFAFB] rounded-xl p-4 text-sm text-[#4B5563] leading-relaxed space-y-3">
          <p>As a CareersPOV Career Mentor, I understand that I will be interacting with school students as part of career exploration experiences.</p>
          <p className="font-medium text-[#1F2937]">I agree to:</p>
          <ul className="space-y-1.5 ml-1">
            {(doc?.points || []).map((t, i) => (
              <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#F97D6A] shrink-0" /><span>{t}</span></li>
            ))}
          </ul>
          <p className="text-xs text-[#9CA3AF] pt-1">{doc?.statement}</p>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={accept} disabled={accepting || !doc} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full" data-testid="accept-declaration-btn">
            {accepting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />} I Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_OFFLINE = {
  career_slug: '', workplace_name: '', address: '', city: '', date: '', start_time: '', end_time: '',
  contact_name: '', contact_phone: '', visit_instructions: '', supervised_by: 'mentor', host_name: '',
};

export function OfflineOpportunityDialog({ open, onOpenChange, careers, onDone }) {
  const [form, setForm] = useState(EMPTY_OFFLINE);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.career_slug || !form.workplace_name.trim() || !form.address.trim() || !form.city.trim() || !form.date || !form.start_time || !form.end_time) {
      toast.error('Please fill all required fields'); return;
    }
    if (form.supervised_by === 'workplace_host' && !form.host_name.trim()) {
      toast.error('Name the approved workplace host who will supervise'); return;
    }
    setSaving(true);
    try {
      await api.post('/mentor-profile/offline-capability', form);
      toast.success('Workplace visit added');
      setForm(EMPTY_OFFLINE);
      onOpenChange(false);
      onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md max-h-[85vh] overflow-y-auto" data-testid="offline-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-[#1F2937]">Offer a Workplace Visit</DialogTitle>
          <DialogDescription>Allow students to visit your workplace for a career experience.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-[#1F2937] font-medium text-sm">Career *</Label>
            <Select value={form.career_slug} onValueChange={v => setForm({ ...form, career_slug: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-career-select"><SelectValue placeholder="Select career" /></SelectTrigger>
              <SelectContent>{(careers || []).map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[#1F2937] font-medium text-sm">Office / Workplace Name *</Label>
            <Input value={form.workplace_name} onChange={e => setForm({ ...form, workplace_name: e.target.value })} placeholder="e.g. Apollo Hospital" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-workplace" /></div>
          <div><Label className="text-[#1F2937] font-medium text-sm">Office Address *</Label>
            <Textarea value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Full address" className="mt-1.5 rounded-xl border-gray-200 min-h-[60px] resize-none" data-testid="offline-address" /></div>
          <div><Label className="text-[#1F2937] font-medium text-sm">City *</Label>
            <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="e.g. Mumbai" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-city" /></div>
          <div><Label className="text-[#1F2937] font-medium text-sm">Date *</Label>
            <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} min={todayLocal()} className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-date" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[#1F2937] font-medium text-sm">Start Time *</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-start-time" /></div>
            <div><Label className="text-[#1F2937] font-medium text-sm">End Time *</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-end-time" /></div>
          </div>
          <div>
            <Label className="text-[#1F2937] font-medium text-sm">Who supervises the students?</Label>
            <Select value={form.supervised_by} onValueChange={v => setForm({ ...form, supervised_by: v })}>
              <SelectTrigger className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-supervisor-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mentor">I will supervise them myself</SelectItem>
                <SelectItem value="workplace_host">An approved workplace host will</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.supervised_by === 'workplace_host' && (
            <div><Label className="text-[#1F2937] font-medium text-sm">Workplace host name *</Label>
              <Input value={form.host_name} onChange={e => setForm({ ...form, host_name: e.target.value })} placeholder="Who will receive and supervise the students" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-host-name" /></div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[#1F2937] font-medium text-sm">Contact person</Label>
              <Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} placeholder="e.g. Reception" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-contact-name" /></div>
            <div><Label className="text-[#1F2937] font-medium text-sm">Contact number</Label>
              <Input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })} placeholder="Phone families can call" className="mt-1.5 rounded-xl h-11 border-gray-200" data-testid="offline-contact-phone" /></div>
          </div>
          <div><Label className="text-[#1F2937] font-medium text-sm">Visit instructions</Label>
            <Textarea value={form.visit_instructions} onChange={e => setForm({ ...form, visit_instructions: e.target.value })} placeholder="Entry gate, ID requirements, parking, what to bring..." className="mt-1.5 rounded-xl border-gray-200 min-h-[60px] resize-none" data-testid="offline-instructions" /></div>
          <div className="bg-amber-50 rounded-xl p-3 flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-[11px] text-amber-700 leading-relaxed">You or an approved workplace host must supervise the students for the whole visit. Parents handle drop-off and pick-up unless the school arranges transport. Recommended: 3–4 hours.</p>
          </div>
        </div>
        <DialogFooter className="gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-full" data-testid="offline-save-btn">{saving ? 'Adding...' : 'Add Opportunity'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
